import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import { createSelector } from '@ngrx/store';
import { ConfirmContent } from '@shared/services/content/content-jsons/confirm-content';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { selectInheritedIraState } from '@store/selectors/inherited-ira/inherited-ira.selectors';
import { selectOpenAccountState } from '@store/selectors/open-account/open-account.selectors';
import { selectInvestmentObjectivesState } from 'vg-interaction-model/dist';

import { buildInvestmentObjectivesDescription } from '../new-account/new-account.cashier';
import {
  selectEmploymentReceipt,
  selectProfileReceipt,
} from '../new-account/new-account.selectors';

export const selectInheritedIraOriginalAccountReceipt = (
  editable: boolean,
  content: typeof ReviewContent.receiptContent | typeof ConfirmContent.receiptContent,
) =>
  createSelector(selectInheritedIraState, ({ isPreviouslyInherited, isRothIra, decedentName }) => ({
    header: content.openInheritedIraAccount.originalAccountLabel,
    editable: editable,
    editRoute: 'secure/inherited-ira/decedent-info',
    sectionEvidenceID: 'originalAccount',
    rows: [
      {
        label: content.openInheritedIraAccount.previouslyInheritedLabel,
        values: [
          isPreviouslyInherited
            ? content.openInheritedIraAccount.yes
            : content.openInheritedIraAccount.no,
        ],
        evidenceID: 'previouslyInherited',
      },
      {
        label: content.openInheritedIraAccount.iraTypeLabel,
        values: [
          content.openInheritedIraAccount[
            isPreviouslyInherited ? 'previouslyInheritedIraTypes' : 'notPreviouslyInheritedIraTypes'
          ][isRothIra ? 'roth' : 'nonRoth'],
        ],
        evidenceID: 'iraType',
      },
      {
        label: isPreviouslyInherited
          ? content.openInheritedIraAccount.originalDecedentNameLabel
          : content.openInheritedIraAccount.decedentNameLabel,
        values: [decedentName],
        evidenceID: 'decedentName',
      },
    ],
  }));

export const selectInheritedIraNewAccountReceipt = (
  editable: boolean,
  content: typeof ReviewContent.receiptContent | typeof ConfirmContent.receiptContent,
) =>
  createSelector(
    selectInheritedIraState,
    selectOpenAccountState,
    selectInvestmentObjectivesState,
    (inheritedIraState, openAccountState, investmentObjectivesState) => ({
      header: content.openInheritedIraAccount.newAccountLabel,
      editable: editable,
      editRoute: 'secure/inherited-ira/settlement-fund',
      sectionEvidenceID: 'newAccount',
      rows: [
        {
          label: content.transferFromAndTo.accountTypeLabel,
          values: [
            inheritedIraState.isRothIra
              ? content.openInheritedIraAccount.inheritedRothIRA
              : content.openInheritedIraAccount.inheritedIRA,
          ],
          evidenceID: 'accountType',
        },
        {
          label: content.transferFromAndTo.accountNumberLabel,
          values: [
            openAccountState.openAccountResponse?.brokerageAccountNumber ??
              content.transferFromAndTo.accountNumberPendingLabel,
          ],
          evidenceID: 'accountNumber',
        },
        {
          label: content.transferFromAndTo.dividendsAndCapitalGainsLabel,
          values: [content.transferFromAndTo.reinvestDistributions],
          modal: 'divcapgains-modal',
          evidenceID: 'divAndCap',
        },
        {
          label: content.openInheritedIraAccount.sourceOfFunds,
          values: [content.openInheritedIraAccount.inheritance],
          evidenceID: 'sourceOfFunds',
        },
        {
          label: content.transferFromAndTo.settlementFundLabel,
          values: [
            SettlementFundMap[openAccountState.settlementFundChoice ?? SettlementFundAoAEnum.FED],
          ].filter((value): value is string => value !== undefined),
          evidenceID: 'settlementFund',
          additionalText: content.transferFromAndTo.settlementFundDescription,
        },
        {
          label: content.financialProfile.investmentObjectiveLabel,
          values: [buildInvestmentObjectivesDescription(investmentObjectivesState)],
          evidenceID: 'investmentObjectives',
        },
      ],
    }),
  );

export const selectInheritedIraReceiptData = (
  editable: boolean,
  content: typeof ReviewContent.receiptContent | typeof ConfirmContent.receiptContent,
) =>
  createSelector(
    selectInheritedIraOriginalAccountReceipt(editable, content),
    selectInheritedIraNewAccountReceipt(editable, content),
    selectProfileReceipt(editable),
    selectEmploymentReceipt(editable),
    (originalAccount, newAccount, profile, employment) => ({
      originalAccount,
      newAccount,
      profile,
      employment,
    }),
  );
